import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  FileText,
  Package,
  ReceiptText,
  ShoppingBag,
  X,
} from "lucide-react";
import ApiURl from "../controllers/Api";
import EmptyCart from "./EmptyCart";
import { gsap } from "gsap";

/* ============================================================
   SL GOLD THEME
   Same dynamic variables as HomeExperience
   ============================================================ */

const PAGE_BG = "var(--user-bg, #E8D9D5)";
const MAROON = "var(--user-maroon, #2B0000)";
const GOLD = "var(--user-gold, #D4AF37)";

const CREAM = "#FBF7F2";
const SOFT_CREAM = "#F6EFEA";
const TEXT_SOFT = "rgba(43, 0, 0, 0.62)";
const BORDER = "rgba(43, 0, 0, 0.14)";
const GOLD_BORDER = "rgba(212, 175, 55, 0.32)";

const OrderSection = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [userId, setUserId] = useState(null);

  const [showReviewPopup, setShowReviewPopup] = useState(false);
  const [review, setReview] = useState("");
  const [rating, setRating] = useState(0);
  const [cancelledOrderId, setCancelledOrderId] = useState(null);

  const [page, setPage] = useState(1);
  const pageSize = 6;

  const navigate = useNavigate();

  const containerRef = useRef(null);
  const headerRef = useRef(null);
  const popupRef = useRef(null);

  /* ============================================================
     STATUS
     ============================================================ */

  const statusMeta = useMemo(
    () => ({
      pending: {
        label: "Pending",
        background: "rgba(212,175,55,0.13)",
        color: "#8A6512",
        border: "rgba(212,175,55,0.38)",
        dot: "#D4AF37",
      },

      completed: {
        label: "Completed",
        background: "rgba(30,110,74,0.08)",
        color: "#326B4F",
        border: "rgba(50,107,79,0.22)",
        dot: "#477B60",
      },

      cancelled: {
        label: "Cancelled",
        background: "rgba(43,0,0,0.055)",
        color: MAROON,
        border: "rgba(43,0,0,0.18)",
        dot: MAROON,
      },
    }),
    []
  );

  /* ============================================================
     GET USER
     ============================================================ */

  useEffect(() => {
    const storedUserId = localStorage.getItem("user_id");

    if (!storedUserId) {
      setError(
        "User not logged in or user_id not found in localStorage"
      );
      setLoading(false);
      return;
    }

    setUserId(storedUserId);
  }, []);

  /* ============================================================
     FETCH ORDERS
     ============================================================ */

  useEffect(() => {
    if (!userId) return;

    let isMounted = true;

    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(
          `${ApiURl}/getOrder.php?user_id=${userId}`
        );

        if (!response.ok) {
          throw new Error(
            `HTTP error! Status: ${response.status}`
          );
        }

        const data = await response.json();

        if (!isMounted) return;

        if (data?.success) {
          const list = Array.isArray(data.data)
            ? data.data
            : [];

          setOrders(list);
          setPage(1);
        } else {
          throw new Error(
            data?.message || "Failed to fetch orders"
          );
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchOrders();

    return () => {
      isMounted = false;
    };
  }, [userId]);

  /* ============================================================
     PAGINATION
     ============================================================ */

  const totalPages = Math.max(
    1,
    Math.ceil(orders.length / pageSize)
  );

  const safePage = Math.min(page, totalPages);

  const start = (safePage - 1) * pageSize;

  const pagedOrders = orders.slice(
    start,
    start + pageSize
  );

  const goToPage = (pageNumber) => {
    setPage(
      Math.min(
        Math.max(1, pageNumber),
        totalPages
      )
    );
  };

  /* ============================================================
     ENTRANCE ANIMATION
     ============================================================ */

  useEffect(() => {
    if (!containerRef.current) return;

    const prefersReducedMotion =
      window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

    if (prefersReducedMotion) return;

    const ctx = gsap.context(() => {
      if (headerRef.current) {
        gsap.fromTo(
          headerRef.current,
          {
            y: 15,
            opacity: 0,
          },
          {
            y: 0,
            opacity: 1,
            duration: 0.6,
            ease: "power3.out",
          }
        );
      }

      gsap.fromTo(
        ".order-card-anim",
        {
          y: 18,
          opacity: 0,
        },
        {
          y: 0,
          opacity: 1,
          duration: 0.55,
          stagger: 0.06,
          delay: 0.08,
          ease: "power3.out",
        }
      );
    }, containerRef);

    return () => ctx.revert();
  }, [loading, orders.length, safePage]);

  /* ============================================================
     POPUP ANIMATION
     ============================================================ */

  useEffect(() => {
    if (
      !showReviewPopup ||
      !popupRef.current
    ) {
      return;
    }

    const timeline = gsap.timeline();

    timeline
      .fromTo(
        ".popup-overlay",
        {
          opacity: 0,
        },
        {
          opacity: 1,
          duration: 0.2,
        }
      )
      .fromTo(
        popupRef.current,
        {
          y: 20,
          opacity: 0,
          scale: 0.97,
        },
        {
          y: 0,
          opacity: 1,
          scale: 1,
          duration: 0.35,
          ease: "power3.out",
        },
        "<"
      );

    return () => timeline.kill();
  }, [showReviewPopup]);

  /* ============================================================
     CANCEL ORDER
     ============================================================ */

  const cancelOrder = async (orderId) => {
    const isConfirmed = window.confirm(
      "Are you sure you want to cancel this order?"
    );

    if (!isConfirmed) return;

    try {
      const response = await fetch(
        `${ApiURl}/updateOrderStatus.php`,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify({
            order_id: orderId,
            status: "cancelled",
          }),
        }
      );

      const result = await response.json();

      if (result?.success) {
        setOrders((previousOrders) =>
          previousOrders.map((order) =>
            order.order_id === orderId
              ? {
                ...order,
                status: "cancelled",
              }
              : order
          )
        );

        setCancelledOrderId(orderId);
        setShowReviewPopup(true);
      } else {
        alert(
          result?.message ||
          "Failed to cancel order."
        );
      }
    } catch (error) {
      alert(
        "An error occurred while cancelling the order."
      );
    }
  };

  /* ============================================================
     REVIEW
     ============================================================ */

  const handleReviewSubmit = async (event) => {
    event.preventDefault();

    if (rating === 0) {
      alert("Please provide a star rating.");
      return;
    }

    const reviewData = {
      order_id: cancelledOrderId,
      user_id: userId,
      review,
      rating,
    };

    try {
      const response = await fetch(
        `${ApiURl}/submitReview.php`,
        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
          },

          body: JSON.stringify(reviewData),
        }
      );

      if (!response.ok) {
        throw new Error(
          `HTTP error! Status: ${response.status}`
        );
      }

      const result = await response.json();

      if (result?.success) {
        setShowReviewPopup(false);
        setReview("");
        setRating(0);

        alert(
          "Thank you for your feedback!"
        );
      } else {
        alert(
          result?.message ||
          "Failed to submit review."
        );
      }
    } catch (error) {
      alert(
        "An error occurred while submitting the review. Please try again later."
      );

      console.error(error);
    }
  };

  /* ============================================================
     STATUS BADGE
     ============================================================ */

  const StatusBadge = ({ status }) => {
    const meta =
      statusMeta[status] ||
      statusMeta.pending;

    return (
      <span
        className="
          inline-flex
          items-center
          gap-2

          rounded-full
          border

          px-3
          py-1.5

          text-[11px]
          font-semibold
          tracking-[0.03em]
        "
        style={{
          color: meta.color,
          backgroundColor: meta.background,
          borderColor: meta.border,
        }}
      >
        <span
          className="
            h-[5px]
            w-[5px]
            rounded-full
          "
          style={{
            backgroundColor: meta.dot,
          }}
        />

        {meta.label}
      </span>
    );
  };

  /* ============================================================
     PAGINATION
     ============================================================ */

  const Pagination = () => {
    if (orders.length <= pageSize) {
      return null;
    }

    const canPrev = safePage > 1;
    const canNext =
      safePage < totalPages;

    return (
      <div
        className="
          mt-7
          flex
          flex-col
          items-center
          justify-between
          gap-4

          sm:flex-row
        "
      >
        <p
          className="
            text-[12px]
            sm:text-[13px]
          "
          style={{
            color: TEXT_SOFT,
          }}
        >
          Showing{" "}
          <span
            className="font-semibold"
            style={{
              color: MAROON,
            }}
          >
            {orders.length === 0
              ? 0
              : start + 1}
            –
            {Math.min(
              start + pageSize,
              orders.length
            )}
          </span>{" "}
          of{" "}
          <span
            className="font-semibold"
            style={{
              color: MAROON,
            }}
          >
            {orders.length}
          </span>{" "}
          orders
        </p>

        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!canPrev}
            onClick={() =>
              goToPage(safePage - 1)
            }
            className="
              flex
              h-9
              w-9
              items-center
              justify-center
              rounded-full
              border
              transition-opacity

              disabled:cursor-not-allowed
              disabled:opacity-30
            "
            style={{
              color: MAROON,
              borderColor: BORDER,
              backgroundColor: CREAM,
            }}
          >
            <ChevronLeft size={16} />
          </button>

          <div
            className="
              min-w-[70px]
              rounded-full
              border
              px-4
              py-2
              text-center
              text-xs
              font-semibold
            "
            style={{
              color: MAROON,
              borderColor: GOLD_BORDER,
              backgroundColor:
                "rgba(212,175,55,0.08)",
            }}
          >
            {safePage} / {totalPages}
          </div>

          <button
            type="button"
            disabled={!canNext}
            onClick={() =>
              goToPage(safePage + 1)
            }
            className="
              flex
              h-9
              w-9
              items-center
              justify-center
              rounded-full
              border
              transition-opacity

              disabled:cursor-not-allowed
              disabled:opacity-30
            "
            style={{
              color: MAROON,
              borderColor: BORDER,
              backgroundColor: CREAM,
            }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  };

  /* ============================================================
     LOADING
     ============================================================ */

  if (loading) {
    return (
      <section
        className="
          min-h-[65vh]
          w-full

          px-4
          py-12

          sm:px-6
          sm:py-16

          lg:px-12
        "
        style={{
          backgroundColor: PAGE_BG,
        }}
      >
        <div className="mx-auto max-w-[1250px]">

          <div
            className="
              overflow-hidden
              rounded-[20px]
              border
            "
            style={{
              borderColor: BORDER,
              backgroundColor: CREAM,
            }}
          >
            <div className="p-6 sm:p-8">
              <div
                className="
                  h-3
                  w-24
                  animate-pulse
                  rounded-full
                "
                style={{
                  backgroundColor:
                    "rgba(43,0,0,0.10)",
                }}
              />

              <div
                className="
                  mt-4
                  h-9
                  w-48
                  animate-pulse
                  rounded
                "
                style={{
                  backgroundColor:
                    "rgba(43,0,0,0.10)",
                }}
              />

              <div
                className="
                  mt-3
                  h-3
                  w-64
                  max-w-full
                  animate-pulse
                  rounded
                "
                style={{
                  backgroundColor:
                    "rgba(43,0,0,0.07)",
                }}
              />
            </div>

            <div
              className="
                grid
                gap-3
                border-t
                p-4

                md:grid-cols-2

                lg:grid-cols-3
                lg:p-6
              "
              style={{
                borderColor: BORDER,
              }}
            >
              {[...Array(6)].map(
                (_, index) => (
                  <div
                    key={index}
                    className="
                      h-[190px]
                      animate-pulse
                      rounded-[16px]
                    "
                    style={{
                      backgroundColor:
                        "rgba(43,0,0,0.055)",
                    }}
                  />
                )
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  /* ============================================================
     ERROR
     ============================================================ */

  if (error) {
    return (
      <section
        className="
          min-h-[65vh]
          w-full

          px-4
          py-12

          sm:px-6
          sm:py-16
        "
        style={{
          backgroundColor: PAGE_BG,
        }}
      >
        <div className="mx-auto max-w-[1100px]">
          <div
            className="
              rounded-[20px]
              border
              p-6

              sm:p-8
            "
            style={{
              backgroundColor: CREAM,
              borderColor: BORDER,
            }}
          >
            <h2
              className="
                font-serif
                text-2xl
              "
              style={{
                color: MAROON,
              }}
            >
              Something went wrong
            </h2>

            <p
              className="mt-2 text-sm"
              style={{
                color: TEXT_SOFT,
              }}
            >
              {error}
            </p>

            <div className="mt-7">
              <EmptyCart />
            </div>
          </div>
        </div>
      </section>
    );
  }

  /* ============================================================
     MAIN UI
     ============================================================ */

  return (
    <section
      ref={containerRef}
      className="
        relative
        min-h-[70vh]
        w-full

        px-3
        py-10

        sm:px-6
        sm:py-14

        lg:px-12
        lg:py-16
      "
      style={{
        backgroundColor: PAGE_BG,
      }}
    >
      <div
        className="
          relative
          mx-auto
          w-full
          max-w-[1250px]
        "
      >
        {/* ======================================================
            HEADING
            Inspired by HomeExperience
            ====================================================== */}

        <div
          ref={headerRef}
          className="
            mb-8
            text-center

            sm:mb-10
          "
        >
          <div
            className="
              mb-3
              flex
              items-center
              justify-center
              gap-3
            "
          >
            <span
              className="
                h-px
                w-7
                sm:w-9
              "
              style={{
                backgroundColor: MAROON,
                opacity: 0.55,
              }}
            />

            <span
              className="
                text-[9px]
                font-semibold
                uppercase
                tracking-[0.3em]

                sm:text-[10px]
              "
              style={{
                color: MAROON,
              }}
            >
              SL Gold
            </span>

            <span
              className="
                h-px
                w-7
                sm:w-9
              "
              style={{
                backgroundColor: MAROON,
                opacity: 0.55,
              }}
            />
          </div>

          <h1
            className="
              font-serif

              text-[34px]
              font-medium
              leading-[1.05]
              tracking-[-0.025em]

              sm:text-[39px]

              md:text-[44px]

              lg:text-[48px]
            "
            style={{
              color: MAROON,
            }}
          >
            My Orders
          </h1>

          <p
            className="
              mx-auto
              mt-2
              max-w-[570px]

              font-serif
              text-[14px]
              leading-[1.55]

              sm:text-[16px]

              md:text-[17px]
            "
            style={{
              color: MAROON,
              opacity: 0.68,
            }}
          >
            Track your purchases and manage
            your SL Gold orders.
          </p>

          <button
            type="button"
            onClick={() =>
              navigate("/all-products")
            }
            className="
              group
              mt-5
              inline-flex
              items-center
              justify-center
              gap-2

              rounded-full
              border

              px-5
              py-2.5

              text-[11px]
              font-semibold
              uppercase
              tracking-[0.12em]

              transition-all
              duration-300

              hover:gap-3
            "
            style={{
              color: GOLD,
              backgroundColor: MAROON,
              borderColor: MAROON,
            }}
          >
            Continue Shopping

            <ArrowRight
              size={15}
              strokeWidth={1.5}
            />
          </button>
        </div>

        {/* ======================================================
            ORDERS CONTAINER
            ====================================================== */}

        {orders.length === 0 ? (
          <div
            className="
              mx-auto
              max-w-[700px]

              rounded-[20px]
              border

              px-5
              py-12

              text-center

              sm:px-10
              sm:py-14
            "
            style={{
              backgroundColor: CREAM,
              borderColor: GOLD_BORDER,
            }}
          >
            <div
              className="
                mx-auto
                flex
                h-14
                w-14
                items-center
                justify-center
                rounded-full
                border
              "
              style={{
                color: GOLD,
                backgroundColor: MAROON,
                borderColor: GOLD_BORDER,
              }}
            >
              <ShoppingBag
                size={23}
                strokeWidth={1.4}
              />
            </div>

            <h2
              className="
                mt-5
                font-serif
                text-[25px]
                font-medium

                sm:text-[30px]
              "
              style={{
                color: MAROON,
              }}
            >
              No orders yet
            </h2>

            <p
              className="
                mx-auto
                mt-2
                max-w-[420px]
                text-[13px]
                leading-relaxed

                sm:text-sm
              "
              style={{
                color: TEXT_SOFT,
              }}
            >
              Your SL Gold purchases will
              appear here once you place your
              first order.
            </p>

            <button
              type="button"
              onClick={() =>
                navigate("/all-products")
              }
              className="
                mt-6
                inline-flex
                items-center
                gap-2

                rounded-full

                px-6
                py-3

                text-xs
                font-semibold
                uppercase
                tracking-[0.1em]
              "
              style={{
                color: GOLD,
                backgroundColor: MAROON,
              }}
            >
              Browse Products
              <ArrowRight size={15} />
            </button>
          </div>
        ) : (
          <>
            {/* ==================================================
                MOBILE: LUXURY ORDER CARDS
                ================================================== */}

            <div
              className="
                grid
                gap-3

                md:hidden
              "
            >
              {pagedOrders.map((order) => (
                <article
                  key={order.order_id}
                  className="
                    order-card-anim
                    overflow-hidden
                    rounded-[18px]
                    border
                  "
                  style={{
                    backgroundColor: CREAM,
                    borderColor: GOLD_BORDER,
                    boxShadow:
                      "0 10px 30px rgba(43,0,0,0.055)",
                  }}
                >
                  {/* CARD TOP */}

                  <div
                    className="
                      flex
                      items-center
                      justify-between
                      gap-3

                      border-b

                      px-4
                      py-4
                    "
                    style={{
                      borderColor: BORDER,
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="
                          flex
                          h-9
                          w-9
                          items-center
                          justify-center
                          rounded-full
                        "
                        style={{
                          backgroundColor:
                            "rgba(212,175,55,0.12)",
                          color: MAROON,
                        }}
                      >
                        <Package
                          size={17}
                          strokeWidth={1.5}
                        />
                      </div>

                      <div>
                        <p
                          className="
                            text-[9px]
                            font-semibold
                            uppercase
                            tracking-[0.18em]
                          "
                          style={{
                            color: TEXT_SOFT,
                          }}
                        >
                          Order
                        </p>

                        <h3
                          className="
                            font-serif
                            text-[19px]
                            font-semibold
                            leading-tight
                          "
                          style={{
                            color: MAROON,
                          }}
                        >
                          #{order.order_id}
                        </h3>
                      </div>
                    </div>

                    <StatusBadge
                      status={order.status}
                    />
                  </div>

                  {/* CARD BODY */}

                  <div className="px-4 py-4">
                    <div
                      className="
                        flex
                        items-center
                        gap-2

                        text-[12px]
                      "
                      style={{
                        color: TEXT_SOFT,
                      }}
                    >
                      <CalendarDays
                        size={14}
                        strokeWidth={1.5}
                        style={{
                          color: GOLD,
                        }}
                      />

                      {order.date}
                    </div>

                    <div
                      className="
                        mt-4
                        border-t
                        pt-4
                      "
                      style={{
                        borderColor: BORDER,
                      }}
                    >
                      <p
                        className="
                          text-[10px]
                          font-semibold
                          uppercase
                          tracking-[0.16em]
                        "
                        style={{
                          color: TEXT_SOFT,
                        }}
                      >
                        Order Items
                      </p>

                      <p
                        className="
                          mt-1
                          font-serif
                          text-[15px]
                        "
                        style={{
                          color: MAROON,
                        }}
                      >
                        View products inside
                        order details
                      </p>
                    </div>

                    {/* ACTIONS */}

                    <div
                      className="
                        mt-5
                        grid
                        grid-cols-2
                        gap-2
                      "
                    >
                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            `/ViewDetails/${order.order_id}`
                          )
                        }
                        className="
                          flex
                          items-center
                          justify-center
                          gap-2

                          rounded-full

                          px-3
                          py-2.5

                          text-[11px]
                          font-semibold
                        "
                        style={{
                          color: GOLD,
                          backgroundColor: MAROON,
                        }}
                      >
                        <FileText
                          size={14}
                          strokeWidth={1.5}
                        />

                        View Details
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            `/Receipt/${order.order_id}`
                          )
                        }
                        className="
                          flex
                          items-center
                          justify-center
                          gap-2

                          rounded-full
                          border

                          px-3
                          py-2.5

                          text-[11px]
                          font-semibold
                        "
                        style={{
                          color: MAROON,
                          borderColor: GOLD_BORDER,
                          backgroundColor:
                            "rgba(212,175,55,0.06)",
                        }}
                      >
                        <ReceiptText
                          size={14}
                          strokeWidth={1.5}
                        />

                        My Bill
                      </button>
                    </div>

                    {order.status ===
                      "pending" ? (
                      <button
                        type="button"
                        onClick={() =>
                          cancelOrder(
                            order.order_id
                          )
                        }
                        className="
                          mt-2
                          w-full

                          rounded-full
                          border

                          px-4
                          py-2.5

                          text-[11px]
                          font-semibold
                        "
                        style={{
                          color: MAROON,
                          borderColor:
                            "rgba(43,0,0,0.18)",
                          backgroundColor:
                            "transparent",
                        }}
                      >
                        Cancel Order
                      </button>
                    ) : (
                      <div
                        className="
                          mt-2
                          w-full

                          rounded-full
                          border

                          px-4
                          py-2.5

                          text-center
                          text-[11px]
                          font-semibold
                        "
                        style={{
                          color: TEXT_SOFT,
                          borderColor: BORDER,
                          backgroundColor:
                            SOFT_CREAM,
                        }}
                      >
                        {order.status ===
                          "completed"
                          ? "Order Completed"
                          : "Order Cancelled"}
                      </div>
                    )}
                  </div>

                  {/* GOLD DETAIL */}

                  <div
                    className="h-[2px] w-full"
                    style={{
                      background:
                        "linear-gradient(90deg, transparent, rgba(212,175,55,.65), transparent)",
                    }}
                  />
                </article>
              ))}
            </div>

            {/* ==================================================
                DESKTOP TABLE
                ================================================== */}

            <div
              className="
                hidden
                overflow-hidden

                rounded-[20px]
                border

                md:block
              "
              style={{
                backgroundColor: CREAM,
                borderColor: GOLD_BORDER,
                boxShadow:
                  "0 14px 40px rgba(43,0,0,0.055)",
              }}
            >
              {/* TABLE TOP */}

              <div
                className="
                  flex
                  items-center
                  justify-between

                  border-b

                  px-6
                  py-4
                "
                style={{
                  borderColor: BORDER,
                }}
              >
                <div className="flex items-center gap-3">
                  <ShoppingBag
                    size={17}
                    strokeWidth={1.4}
                    style={{
                      color: GOLD,
                    }}
                  />

                  <span
                    className="
                      font-serif
                      text-[17px]
                      font-medium
                    "
                    style={{
                      color: MAROON,
                    }}
                  >
                    Order History
                  </span>
                </div>

                <span
                  className="
                    text-[10px]
                    font-semibold
                    uppercase
                    tracking-[0.18em]
                  "
                  style={{
                    color: TEXT_SOFT,
                  }}
                >
                  {orders.length}{" "}
                  {orders.length === 1
                    ? "Order"
                    : "Orders"}
                </span>
              </div>

              <div className="overflow-x-auto">
                <table
                  className="
                    w-full
                    min-w-[900px]
                    border-collapse
                  "
                >
                  <thead>
                    <tr
                      style={{
                        backgroundColor:
                          "rgba(43,0,0,0.035)",
                      }}
                    >
                      {[
                        "Order",
                        "Date",
                        "Status",
                        "Products",
                        "Actions",
                      ].map(
                        (
                          heading,
                          index
                        ) => (
                          <th
                            key={heading}
                            className={`
                              px-5
                              py-3.5

                              text-[10px]
                              font-semibold
                              uppercase
                              tracking-[0.16em]

                              ${index === 4
                                ? "text-right"
                                : "text-left"
                              }
                            `}
                            style={{
                              color: MAROON,
                            }}
                          >
                            {heading}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>

                  <tbody>
                    {pagedOrders.map(
                      (order) => (
                        <tr
                          key={
                            order.order_id
                          }
                          className="
                            order-card-anim
                            border-t
                            transition-colors
                            duration-200
                          "
                          style={{
                            borderColor:
                              BORDER,
                          }}
                        >
                          {/* ORDER */}

                          <td className="px-5 py-5">
                            <div className="flex items-center gap-3">
                              <div
                                className="
                                  flex
                                  h-9
                                  w-9
                                  items-center
                                  justify-center
                                  rounded-full
                                "
                                style={{
                                  color:
                                    MAROON,

                                  backgroundColor:
                                    "rgba(212,175,55,0.12)",
                                }}
                              >
                                <Package
                                  size={16}
                                  strokeWidth={
                                    1.5
                                  }
                                />
                              </div>

                              <span
                                className="
                                  font-serif
                                  text-[16px]
                                  font-semibold
                                "
                                style={{
                                  color:
                                    MAROON,
                                }}
                              >
                                #
                                {
                                  order.order_id
                                }
                              </span>
                            </div>
                          </td>

                          {/* DATE */}

                          <td
                            className="
                              whitespace-nowrap
                              px-5
                              py-5
                              text-[12px]
                            "
                            style={{
                              color:
                                TEXT_SOFT,
                            }}
                          >
                            {order.date}
                          </td>

                          {/* STATUS */}

                          <td className="px-5 py-5">
                            <StatusBadge
                              status={
                                order.status
                              }
                            />
                          </td>

                          {/* PRODUCTS */}

                          <td
                            className="
                              px-5
                              py-5

                              font-serif
                              text-[14px]
                            "
                            style={{
                              color:
                                TEXT_SOFT,
                            }}
                          >
                            View products
                            inside order
                            details
                          </td>

                          {/* ACTIONS */}

                          <td className="px-5 py-5">
                            <div
                              className="
                                flex
                                items-center
                                justify-end
                                gap-2
                                whitespace-nowrap
                              "
                            >
                              <button
                                type="button"
                                onClick={() =>
                                  navigate(
                                    `/ViewDetails/${order.order_id}`
                                  )
                                }
                                className="
                                  rounded-full

                                  px-4
                                  py-2

                                  text-[10px]
                                  font-semibold

                                  transition-opacity
                                  hover:opacity-90
                                "
                                style={{
                                  color:
                                    GOLD,

                                  backgroundColor:
                                    MAROON,
                                }}
                              >
                                View Details
                              </button>

                              <button
                                type="button"
                                onClick={() =>
                                  navigate(
                                    `/Receipt/${order.order_id}`
                                  )
                                }
                                className="
                                  rounded-full
                                  border

                                  px-4
                                  py-2

                                  text-[10px]
                                  font-semibold
                                "
                                style={{
                                  color:
                                    MAROON,

                                  borderColor:
                                    GOLD_BORDER,

                                  backgroundColor:
                                    "rgba(212,175,55,0.06)",
                                }}
                              >
                                My Bill
                              </button>

                              {order.status ===
                                "pending" ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    cancelOrder(
                                      order.order_id
                                    )
                                  }
                                  className="
                                    rounded-full
                                    border

                                    px-4
                                    py-2

                                    text-[10px]
                                    font-semibold
                                  "
                                  style={{
                                    color:
                                      MAROON,

                                    borderColor:
                                      "rgba(43,0,0,0.18)",

                                    backgroundColor:
                                      "transparent",
                                  }}
                                >
                                  Cancel
                                </button>
                              ) : (
                                <span
                                  className="
                                    rounded-full
                                    border

                                    px-4
                                    py-2

                                    text-[10px]
                                    font-semibold
                                  "
                                  style={{
                                    color:
                                      TEXT_SOFT,

                                    borderColor:
                                      BORDER,

                                    backgroundColor:
                                      SOFT_CREAM,
                                  }}
                                >
                                  {order.status ===
                                    "completed"
                                    ? "Completed"
                                    : "Cancelled"}
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <Pagination />
          </>
        )}
      </div>

      {/* ========================================================
          REVIEW POPUP
          ======================================================== */}

      {showReviewPopup && (
        <div
          className="
            popup-overlay

            fixed
            inset-0
            z-50

            flex
            items-center
            justify-center

            bg-black/50
            px-4

            backdrop-blur-[2px]
          "
        >
          <div
            ref={popupRef}
            className="
              w-full
              max-w-[430px]

              overflow-hidden
              rounded-[20px]
              border
            "
            style={{
              backgroundColor: CREAM,
              borderColor: GOLD_BORDER,

              boxShadow:
                "0 25px 80px rgba(20,0,0,0.32)",
            }}
          >
            {/* POPUP HEADER */}

            <div
              className="
                flex
                items-center
                justify-between

                border-b

                px-5
                py-4
              "
              style={{
                backgroundColor: MAROON,
                borderColor:
                  "rgba(212,175,55,0.25)",
              }}
            >
              <div>
                <p
                  className="
                    text-[9px]
                    font-semibold
                    uppercase
                    tracking-[0.2em]
                  "
                  style={{
                    color: GOLD,
                  }}
                >
                  SL Gold
                </p>

                <h3
                  className="
                    mt-0.5
                    font-serif
                    text-[22px]
                    font-medium
                  "
                  style={{
                    color: "#ffffff",
                  }}
                >
                  Your Feedback
                </h3>
              </div>

              <button
                type="button"
                onClick={() =>
                  setShowReviewPopup(false)
                }
                className="
                  flex
                  h-8
                  w-8
                  items-center
                  justify-center

                  rounded-full
                  border
                "
                style={{
                  color: GOLD,
                  borderColor:
                    "rgba(212,175,55,0.32)",
                }}
                aria-label="Close"
              >
                <X
                  size={15}
                  strokeWidth={1.5}
                />
              </button>
            </div>

            {/* POPUP BODY */}

            <div className="px-5 py-6 sm:px-7">
              <div className="text-center">
                <h4
                  className="
                    font-serif
                    text-[22px]
                    font-medium
                  "
                  style={{
                    color: MAROON,
                  }}
                >
                  We’re sorry to see you
                  cancel.
                </h4>

                <p
                  className="
                    mx-auto
                    mt-2
                    max-w-[310px]

                    text-[12px]
                    leading-relaxed
                  "
                  style={{
                    color: TEXT_SOFT,
                  }}
                >
                  Please tell us about your
                  experience so we can serve
                  you better.
                </p>
              </div>

              {/* STARS */}

              <div
                className="
                  mt-5
                  flex
                  justify-center
                  gap-1.5
                "
              >
                {[...Array(5)].map(
                  (_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() =>
                        setRating(
                          index + 1
                        )
                      }
                      className="
                        text-[29px]
                        leading-none
                        transition-transform
                        duration-200

                        hover:scale-110
                      "
                      style={{
                        color:
                          rating > index
                            ? GOLD
                            : "rgba(43,0,0,0.15)",
                      }}
                      aria-label={`Rate ${index + 1
                        } star`}
                    >
                      ★
                    </button>
                  )
                )}
              </div>

              <form
                onSubmit={
                  handleReviewSubmit
                }
                className="mt-5"
              >
                <textarea
                  value={review}
                  onChange={(event) =>
                    setReview(
                      event.target.value
                    )
                  }
                  placeholder="Write your feedback (optional)"
                  className="
                    h-28
                    w-full
                    resize-none

                    rounded-[14px]
                    border

                    px-4
                    py-3

                    text-[13px]

                    outline-none
                  "
                  style={{
                    color: MAROON,
                    borderColor: BORDER,
                    backgroundColor:
                      SOFT_CREAM,
                  }}
                />

                <button
                  type="submit"
                  className="
                    mt-4
                    w-full

                    rounded-full

                    px-6
                    py-3

                    text-[11px]
                    font-semibold
                    uppercase
                    tracking-[0.12em]
                  "
                  style={{
                    color: GOLD,
                    backgroundColor:
                      MAROON,
                  }}
                >
                  Submit Feedback
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default OrderSection;